/**
 * The safety and security registers as the platform sends them
 * (routers/safety.py, routers/security.py), and their translation into the
 * web app's names.
 *
 * Read leniently where a missing field only costs a label, and strictly where
 * it decides what a reader is told: a risk without a `state` is refused,
 * because a verdict with no state is exactly the blank that reads as "fine".
 */
import { z } from "zod";
import type {
  AssessedHazard,
  Confidentiality,
  ControlType,
  FiledReport,
  Hazard,
  HazardAction,
  HazardAssessment,
  HazardDetail,
  HazardPage,
  HazardStatus,
  IncidentCategory,
  IncidentStatus,
  RaisedIncident,
  ReporterState,
  ReportStatus,
  RiskVerdict,
  SafetyMatrix,
  SafetyReport,
  SafetyReportPage,
  SafetyVocabulary,
  SecurityIncident,
  SecurityIncidentPage,
  Tolerability,
} from "../domain/safety";

const str = z.string().nullish();
const num = z.number().nullish();
const bool = z.boolean().nullish();
const opt = (v: string | null | undefined): string | undefined => (v == null || v === "" ? undefined : v);

/* ── vocabulary ─────────────────────────────────────────────────────────── */

const wireLevel = z.object({ code: z.union([z.string(), z.number()]), name: str, rank: num }).passthrough();

export const wireVocabulary = z
  .object({
    matrices: z.array(
      z
        .object({
          key: z.string(),
          version: z.union([z.string(), z.number()]),
          name: str,
          likelihood: z.array(wireLevel),
          severity: z.array(wireLevel),
          cells: z.record(z.string(), z.string()),
          review_months: z.record(z.string(), z.number().nullable()).nullish(),
        })
        .passthrough(),
    ),
    hazard_categories: z.array(
      z.object({ key: z.string(), name: str, description: str, domain: str, default_matrix: str }).passthrough(),
    ),
    issues: z.array(z.string()).nullish(),
  })
  .passthrough();

const level = (l: z.infer<typeof wireLevel>) => ({
  code: String(l.code),
  name: l.name ?? String(l.code),
  rank: l.rank ?? 0,
});

export function fromWireVocabulary(v: z.infer<typeof wireVocabulary>): SafetyVocabulary {
  return {
    matrices: v.matrices.map(
      (m): SafetyMatrix => ({
        key: m.key,
        version: String(m.version),
        name: m.name ?? m.key,
        likelihood: m.likelihood.map(level),
        severity: m.severity.map(level),
        cells: m.cells as Record<string, Tolerability>,
        reviewMonths: Object.fromEntries(
          Object.entries(m.review_months ?? {}).filter((e): e is [string, number] => e[1] != null),
        ),
      }),
    ),
    categories: v.hazard_categories.map((c) => ({
      key: c.key,
      name: c.name ?? c.key,
      description: opt(c.description),
      domain: opt(c.domain),
      defaultMatrix: opt(c.default_matrix),
    })),
    issues: v.issues ?? [],
  };
}

/* ── hazards ────────────────────────────────────────────────────────────── */

const wireRisk = z
  .object({
    state: z.enum(["never_assessed", "unclassified", "stale", "assessed"]),
    tolerability: str,
    risk_index: str,
    likelihood: z.union([z.string(), z.number()]).nullish(),
    severity: str,
    kind: str,
    matrix: str,
    assessed_at: str,
    why: str,
  })
  .passthrough();

const NEVER: RiskVerdict = { state: "never_assessed", tolerability: null };

function risk(r: z.infer<typeof wireRisk> | null | undefined): RiskVerdict {
  if (!r) return NEVER;
  return {
    state: r.state,
    tolerability: (r.tolerability ?? null) as Tolerability | null,
    riskIndex: opt(r.risk_index),
    likelihood: r.likelihood == null ? undefined : String(r.likelihood),
    severity: opt(r.severity),
    kind: opt(r.kind) as RiskVerdict["kind"],
    matrix: opt(r.matrix),
    assessedAt: opt(r.assessed_at),
    why: r.why ?? null,
  };
}

const wireReview = z
  .object({ state: z.string(), due: str, days_overdue: num, days_remaining: num })
  .passthrough();

export const wireHazard = z
  .object({
    id: z.string(),
    reference: z.string(),
    category: z.string(),
    domain: str,
    title: z.string(),
    description: str,
    status: z.string(),
    origin: str,
    site_id: str,
    zone_code: str,
    owner_id: str,
    owner_name: str,
    owner_label: str,
    area: str,
    identified_at: z.string(),
    review_due: str,
    closed_at: str,
    open_actions: num,
    overdue_actions: num,
    risk_accepted: bool,
    demo: bool,
    risk: wireRisk,
    initial_risk: wireRisk.nullish(),
    controls: z.array(z.string()).nullish(),
    review: wireReview.nullish(),
  })
  .passthrough();

export function fromWireHazard(h: z.infer<typeof wireHazard>): Hazard {
  return {
    id: h.id,
    reference: h.reference,
    category: h.category,
    domain: opt(h.domain),
    title: h.title,
    description: opt(h.description),
    status: h.status as HazardStatus,
    origin: h.origin ?? "report",
    siteId: opt(h.site_id),
    zoneCode: opt(h.zone_code),
    ownerId: opt(h.owner_id),
    ownerName: opt(h.owner_name),
    ownerLabel: opt(h.owner_label),
    area: opt(h.area),
    identifiedAt: h.identified_at,
    reviewDue: opt(h.review_due),
    closedAt: opt(h.closed_at),
    openActions: h.open_actions ?? 0,
    overdueActions: h.overdue_actions ?? 0,
    riskAccepted: !!h.risk_accepted,
    demo: !!h.demo,
    risk: risk(h.risk),
    initialRisk: risk(h.initial_risk),
    controls: h.controls ?? [],
    review: {
      state: (h.review?.state ?? "no_review_date") as Hazard["review"]["state"],
      due: h.review?.due ?? null,
      daysOverdue: h.review?.days_overdue ?? undefined,
      daysRemaining: h.review?.days_remaining ?? undefined,
    },
  };
}

export const wireHazardPage = z
  .object({ hazards: z.array(wireHazard), register_total: num, truncated: bool })
  .passthrough();

export function fromWireHazardPage(p: z.infer<typeof wireHazardPage>): HazardPage {
  return { items: p.hazards.map(fromWireHazard), total: p.register_total ?? p.hazards.length, truncated: !!p.truncated };
}

export const wireHazardDetail = wireHazard.extend({
  assessments: z
    .array(
      z
        .object({
          kind: z.string(),
          likelihood: z.union([z.string(), z.number()]),
          severity: z.string(),
          risk_index: z.string(),
          tolerability: z.string(),
          matrix_key: z.string(),
          matrix_version: z.union([z.string(), z.number()]),
          rationale: str,
          assessed_at: z.string(),
        })
        .passthrough(),
    )
    .nullish(),
  actions: z
    .array(
      z
        .object({
          seq: z.number(),
          description: z.string(),
          control_type: str,
          due_date: str,
          status: z.string(),
          completed_at: str,
          overdue: bool,
        })
        .passthrough(),
    )
    .nullish(),
  reports: z
    .array(
      z.object({ id: z.string(), reference: z.string(), status: z.string(), reported_at: z.string(), confidentiality: z.string() }).passthrough(),
    )
    .nullish(),
  acceptances: z
    .array(z.object({ accepted_at: str, rationale: str, risk_index: str, tolerability: str }).passthrough())
    .nullish(),
  closure_note: str,
  // the individual read has no `review` computed twice; the listing's shape covers it
});

export function fromWireHazardDetail(d: z.infer<typeof wireHazardDetail>): HazardDetail {
  return {
    ...fromWireHazard(d),
    assessments: (d.assessments ?? []).map(
      (a): HazardAssessment => ({
        kind: a.kind as HazardAssessment["kind"],
        likelihood: String(a.likelihood),
        severity: a.severity,
        riskIndex: a.risk_index,
        tolerability: a.tolerability as Tolerability,
        matrix: `${a.matrix_key}@${a.matrix_version}`,
        rationale: opt(a.rationale),
        assessedAt: a.assessed_at,
      }),
    ),
    actions: (d.actions ?? []).map(
      (a): HazardAction => ({
        seq: a.seq,
        description: a.description,
        controlType: (a.control_type ?? "administrative") as ControlType,
        dueDate: opt(a.due_date),
        status: a.status as HazardAction["status"],
        completedAt: opt(a.completed_at),
        overdue: !!a.overdue,
      }),
    ),
    reports: (d.reports ?? []).map((r) => ({
      id: r.id,
      reference: r.reference,
      status: r.status as ReportStatus,
      reportedAt: r.reported_at,
      confidentiality: r.confidentiality as Confidentiality,
    })),
    acceptances: (d.acceptances ?? []).map((a) => ({
      acceptedAt: a.accepted_at ?? "",
      rationale: a.rationale ?? "",
      riskIndex: opt(a.risk_index),
      tolerability: opt(a.tolerability),
    })),
    closureNote: opt(d.closure_note),
  };
}

export const wireAssessed = z
  .object({
    status: z.string(),
    review_due: str,
    escalated: bool,
    warnings: z.array(z.string()).nullish(),
    risk: z.object({ risk_index: z.string(), tolerability: z.string() }).passthrough(),
  })
  .passthrough();

export function fromWireAssessed(a: z.infer<typeof wireAssessed>): AssessedHazard {
  return {
    status: a.status as HazardStatus,
    risk: { riskIndex: a.risk.risk_index, tolerability: a.risk.tolerability as Tolerability },
    reviewDue: opt(a.review_due),
    escalated: !!a.escalated,
    warnings: a.warnings ?? [],
  };
}

/* ── reports ────────────────────────────────────────────────────────────── */

const wireReporter = z
  .object({ state: z.enum(["named", "withheld", "none"]), display_name: str, why: str })
  .passthrough();

export const wireReport = z
  .object({
    id: z.string(),
    reference: z.string(),
    confidentiality: z.enum(["open", "confidential", "anonymous"]),
    mandatory: bool,
    category: str,
    title: z.string(),
    narrative: str,
    occurred_at: str,
    reported_at: z.string(),
    status: z.string(),
    site_id: str,
    hazard_id: str,
    hazard_reference: str,
    outcome: str,
    closed_at: str,
    kind: str,
    immediate: bool,
    register: str,
    where: str,
    attrs: z.record(z.string(), z.unknown()).nullish(),
    demo: bool,
    reporter: wireReporter.nullish(),
  })
  .passthrough();

function reporter(r: z.infer<typeof wireReporter> | null | undefined): ReporterState {
  if (!r) return { state: "withheld" };
  if (r.state === "named") return { state: "named", name: opt(r.display_name) };
  return { state: r.state, why: opt(r.why) };
}

export function fromWireReport(r: z.infer<typeof wireReport>): SafetyReport {
  return {
    id: r.id,
    reference: r.reference,
    confidentiality: r.confidentiality,
    mandatory: !!r.mandatory,
    category: opt(r.category),
    title: r.title,
    narrative: r.narrative ?? undefined,
    occurredAt: opt(r.occurred_at),
    reportedAt: r.reported_at,
    status: r.status as ReportStatus,
    siteId: opt(r.site_id),
    hazardId: opt(r.hazard_id),
    hazardReference: opt(r.hazard_reference),
    outcome: opt(r.outcome),
    closedAt: opt(r.closed_at),
    kind: opt(r.kind),
    immediate: !!r.immediate,
    register: opt(r.register),
    where: opt(r.where),
    attrs: r.attrs ?? undefined,
    demo: !!r.demo,
    reporter: reporter(r.reporter),
  };
}

export const wireReportPage = z
  .object({ reports: z.array(wireReport), filed_total: num, note: str })
  .passthrough();

export function fromWireReportPage(p: z.infer<typeof wireReportPage>): SafetyReportPage {
  return { items: p.reports.map(fromWireReport), total: p.filed_total ?? p.reports.length, note: opt(p.note) };
}

export const wireFiled = z
  .object({
    id: z.string(),
    reference: z.string(),
    status: z.string(),
    reported_at: z.string(),
    confidentiality: z.enum(["open", "confidential", "anonymous"]),
    reporter_recorded: bool,
    note: z.string(),
    replayed: bool,
  })
  .passthrough();

export function fromWireFiled(f: z.infer<typeof wireFiled>): FiledReport {
  return {
    id: f.id,
    reference: f.reference,
    status: f.status as ReportStatus,
    reportedAt: f.reported_at,
    confidentiality: f.confidentiality,
    reporterRecorded: !!f.reporter_recorded,
    note: f.note,
    ...(f.replayed ? { replayed: true } : {}),
  };
}

/* ── security incidents ─────────────────────────────────────────────────── */

export const wireIncidentCategories = z
  .object({
    incident_categories: z.array(
      z
        .object({
          key: z.string(),
          name: str,
          description: str,
          default_severity: num,
          default_classification: str,
          reportable: bool,
          report_to: str,
          report_within_hours: num,
        })
        .passthrough(),
    ),
  })
  .passthrough();

export function fromWireIncidentCategories(v: z.infer<typeof wireIncidentCategories>): IncidentCategory[] {
  return v.incident_categories.map((c) => ({
    key: c.key,
    name: c.name ?? c.key,
    description: opt(c.description),
    defaultSeverity: c.default_severity ?? 3,
    defaultClassification: (c.default_classification ?? "internal") as IncidentCategory["defaultClassification"],
    reportable: !!c.reportable,
    reportTo: opt(c.report_to),
    reportWithinHours: c.report_within_hours ?? undefined,
  }));
}

export const wireIncidentPage = z
  .object({
    incidents: z.array(
      z
        .object({
          id: z.string(),
          reference: z.string(),
          category: z.string(),
          severity: z.number(),
          status: z.string(),
          classification: z.string(),
          title: z.string(),
          zone_code: str,
          detected_at: z.string(),
          occurred_at: str,
          closed_at: str,
          reportable: bool,
          notified_at: str,
          summary: str,
          detail: z.record(z.string(), z.unknown()).nullish(),
          outcome: str,
          reported_by_name: str,
        })
        .passthrough(),
    ),
    includes_restricted: bool,
  })
  .passthrough();

export function fromWireIncidentPage(p: z.infer<typeof wireIncidentPage>): SecurityIncidentPage {
  return {
    items: p.incidents.map(
      (i): SecurityIncident => ({
        id: i.id,
        reference: i.reference,
        category: i.category,
        severity: i.severity,
        status: i.status as IncidentStatus,
        classification: i.classification as SecurityIncident["classification"],
        title: i.title,
        zoneCode: opt(i.zone_code),
        detectedAt: i.detected_at,
        occurredAt: opt(i.occurred_at),
        closedAt: opt(i.closed_at),
        reportable: !!i.reportable,
        notifiedAt: opt(i.notified_at),
        summary: opt(i.summary),
        detail: i.detail ?? {},
        outcome: opt(i.outcome),
        reportedByName: opt(i.reported_by_name),
      }),
    ),
    includesRestricted: i_restricted(p.includes_restricted),
  };
}

const i_restricted = (v: boolean | null | undefined) => v !== false;

export const wireRaised = z
  .object({
    id: z.string(),
    reference: z.string(),
    detected_at: z.string(),
    replayed: bool,
    notification: z
      .object({ required: bool, to: str, within_hours: num, note: str })
      .passthrough()
      .nullish(),
  })
  .passthrough();

export function fromWireRaised(r: z.infer<typeof wireRaised>): RaisedIncident {
  return {
    id: r.id,
    reference: r.reference,
    detectedAt: r.detected_at,
    ...(r.replayed ? { replayed: true } : {}),
    ...(r.notification
      ? {
          notification: {
            required: !!r.notification.required,
            to: opt(r.notification.to),
            withinHours: r.notification.within_hours ?? undefined,
            note: r.notification.note ?? "",
          },
        }
      : {}),
  };
}
